import { FormStore, useStoreState } from '@ariakit/react'
import { ComponentProps, FC, HTMLAttributes } from 'react'

import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { RequiredAsterisk } from '../../../../../atoms/RequiredAsterisk'
import { EmbroideryTextInput } from '../../../../Modal/primitives/EmbroideryTextInput/EmbroideryTextInput'
import {
  EmbroideryInputs,
  EmbroideryOptionWrapper,
  Label,
} from '../../../../Modal/primitives/shared-form-styles'

export type NameInputProps = {
  form: FormStore
  maxCharacterCount: number
  ariaLabelledby?: string
  placeholder?: string
  name: string
} & Omit<ComponentProps<typeof EmbroideryTextInput>, 'onChange' | 'value'>

/**
 * An individual embroidery name input field.
 *
 * @param props
 * @param props.form - The current form store. Used for reading the current value, error, and touched state for the given input name.
 * @param maxCharacterCount - Define a maximum character count for the text input.
 * @param placeholder - The placeholder text that is displayed for the input.
 * @param name - The name attribute for the input. This should correlate to the tracked value in the passed `form` object.
 * @param ariaLabelledBy - The `aria-labelledby` attribute that should be applied to the input. This should match the `label` prop that was passed to `Name.Wrapper`.
 */
export const Input: FC<NameInputProps> = ({
  form,
  maxCharacterCount,
  placeholder,
  name,
  ariaLabelledby,
  ...rest
}) => {
  const touched = useStoreState(form, () => form.getFieldTouched(name))
  const error = useStoreState(form, () => form.getError(name))
  const value = useStoreState(form, s => s.values[name])

  return (
    <EmbroideryTextInput
      ariaLabelledby={ariaLabelledby}
      errorMessage={touched && error ? String(error) : undefined}
      maxCharacterCount={maxCharacterCount}
      name={name}
      onChange={e => {
        form.setValue(name, e.target.value)
      }}
      placeholder={placeholder}
      value={String(value ?? '')}
      {...rest}
    />
  )
}

/**
 * A wrapper for the embroidery name form.
 *
 * Children of this component are expected to be an instance of `<Name.Input />`.
 *
 * @param props
 * @param props.label - The `label` attribute to apply to the label text element. Should match the `ariaLabelledby` prop that is provided to `Name.Input`.
 * @param props.title - The title to display for the name section. Defaults to our predefined fixture for this title value.
 */
export type NameWrapperProps = {
  label: string
  title?: string
  required?: boolean
  disabled?: boolean
} & HTMLAttributes<HTMLDivElement>

export const Wrapper: FC<NameWrapperProps> = ({
  label,
  children,
  title,
  required = false,
  disabled,
  ...rest
}) => {
  const {
    embroidery: {
      modal: { forms: fixtures },
    },
  } = useTranslation()
  return (
    <EmbroideryOptionWrapper {...rest}>
      <Label disabled={disabled} id={label}>
        <span>
          {title ?? fixtures.textOption.textLabel}
          {required && <RequiredAsterisk />}
        </span>
      </Label>
      <EmbroideryInputs>{children}</EmbroideryInputs>
    </EmbroideryOptionWrapper>
  )
}
